import { Provider, Type } from '@angular/core';
import { TestBed, waitForAsync } from '@angular/core/testing';
import { BrowserDynamicTestingModule } from '@angular/platform-browser-dynamic/testing';

import { setProps } from '../internals/query';
import * as customMatchers from '../matchers';
import { BaseSpecOverrides } from '../base/options';
import { addMatchers } from '../core';
import { isType } from '../types';
import { HostComponent } from '../Spec-host/host-component';

import { initialSpecPipeModule } from './initial-module';
import { getSpecPipeDefaultOptions, SpecPipeOptions } from './options';
import { SpecPipe } from './spec-pipe';
import { overrideModules } from '../Spec/create-factory';

/**
 * @publicApi
 */
export type SpecPipeFactory<P, H> = <HP>(
  templateOrOverrides?: string | SpecPipeOverrides<H, HP>,
  overrides?: SpecPipeOverrides<H, HP>
) => SpecPipe<P, H & (HostComponent extends H ? HP : unknown)>;

/**
 * @publicApi
 */
export interface SpecPipeOverrides<H, HP> extends BaseSpecOverrides {
  detectChanges?: boolean;
  hostProps?: HostComponent extends H ? HP : Partial<H>;
}

/**
 * @publicApi
 */
export function createPipeFactory<P, H = HostComponent>(typeOrOptions: Type<P> | SpecPipeOptions<P, H>): SpecPipeFactory<P, H> {
  const options = isType(typeOrOptions)
    ? getSpecPipeDefaultOptions<P, H>({ pipe: typeOrOptions })
    : getSpecPipeDefaultOptions(typeOrOptions);

  const moduleMetadata = initialSpecPipeModule<P, H>(options);

  beforeEach(
    waitForAsync(() => {
      addMatchers(customMatchers);
      TestBed.configureTestingModule(moduleMetadata);
      overrideModules(options);
    })
  );

  return <HP>(templateOrOverrides?: string | SpecPipeOverrides<H, HP>, overrides?: SpecPipeOverrides<H, HP>) => {
    const defaults: SpecPipeOverrides<H, HP> = {
      hostProps: {} as any,
      detectChanges: true,
      providers: []
    };
    const resolvedOverrides = typeof templateOrOverrides === 'object' ? templateOrOverrides : overrides;
    const { detectChanges, hostProps, providers } = { ...defaults, ...resolvedOverrides };
    const template = typeof templateOrOverrides === 'string' ? templateOrOverrides : options.template;

    if (providers && providers.length) {
      providers.forEach((provider: Provider) => {
        TestBed.overrideProvider((provider as any).provide, provider as any);
      });
    }

    if (template) {
      TestBed.overrideModule(BrowserDynamicTestingModule, {
        set: {
          entryComponents: moduleMetadata.entryComponents
        }
      }).overrideComponent(options.host, {
        set: { template }
      });
    }

    const Spec = createSpecPipe(options, hostProps);

    if (options.detectChanges && detectChanges) {
      Spec.detectChanges();
    }

    return Spec;
  };
}

function createSpecPipe<P, H, HP>(options: Required<SpecPipeOptions<P, H>>, hostProps?: HP): SpecPipe<P, H & HP> {
  const hostFixture = TestBed.createComponent(options.host);
  const debugElement = hostFixture.debugElement;

  const hostComponent = setProps(hostFixture.componentInstance, hostProps);

  return new SpecPipe(hostComponent, hostFixture, hostFixture.debugElement, debugElement.nativeElement);
}
